/**
 * SpanProcessor that stamps `user.id` and `client.session.id` on every
 * span at creation time.
 *
 * ## How it works
 *
 * Call `setUserId(id)` after authentication succeeds and `clearUserId()`
 * on sign-out.  Every span created while a user ID is set will
 * automatically receive a `user.id` attribute — including auto-
 * instrumentation spans (fetch, document-load) that application code
 * cannot easily reach.
 *
 * In addition, every span unconditionally receives a `client.session.id`
 * attribute — a random UUID that lives for the lifetime of the browser
 * tab (stored in `sessionStorage`).  This enables grouping spans from
 * the same browsing session without any PII.
 *
 * ## PII policy
 *
 * Only the opaque Better Auth user UUID is stored in `user.id`.
 * `client.session.id` is a random UUID with no link to identity.
 * No email, name, or other PII is ever attached to spans.
 *
 * ## Why a SpanProcessor instead of per-span setAttribute?
 *
 * Auto-instrumentation spans (FetchInstrumentation, DocumentLoad)
 * are created by the SDK — application code has no hook to add
 * attributes to them.  A SpanProcessor's `onStart` callback runs
 * for **every** span, making it the only reliable injection point.
 *
 * @module E4 – User context on browser spans
 */

import type { Context } from "@opentelemetry/api";
import type { Span, ReadableSpan, SpanProcessor } from "@opentelemetry/sdk-trace-base";

// ---------------------------------------------------------------------------
// Client session ID — non-PII, tab-scoped identifier
// ---------------------------------------------------------------------------

const SESSION_STORAGE_KEY = "floline-client-session-id";

/**
 * Return an existing client session ID from `sessionStorage`, or create
 * and store a new random UUID.  The value lives for the lifetime of the
 * browser tab (cleared when the tab is closed).
 *
 * Falls back to an in-memory UUID when `sessionStorage` is unavailable
 * (e.g. in a web-worker or when storage is blocked by the browser).
 */
function getOrCreateClientSessionId(): string {
  try {
    const existing = globalThis.sessionStorage?.getItem(SESSION_STORAGE_KEY);
    if (existing) {
      return existing;
    }
    const id = crypto.randomUUID();
    globalThis.sessionStorage?.setItem(SESSION_STORAGE_KEY, id);
    return id;
  } catch {
    // sessionStorage unavailable — generate and cache in memory.
    if (!memorySessionId) {
      memorySessionId = crypto.randomUUID();
    }
    return memorySessionId;
  }
}

/** In-memory fallback when sessionStorage is inaccessible. */
let memorySessionId: string | undefined;

/** Lazily resolved client session ID (cached after first access). */
let cachedClientSessionId: string | undefined;

function clientSessionId(): string {
  if (!cachedClientSessionId) {
    cachedClientSessionId = getOrCreateClientSessionId();
  }
  return cachedClientSessionId;
}

// ---------------------------------------------------------------------------
// Mutable state — written by setUserId / clearUserId, read by onStart.
// ---------------------------------------------------------------------------

let currentUserId: string | undefined;

// ---------------------------------------------------------------------------
// Public setters
// ---------------------------------------------------------------------------

/**
 * Set the authenticated user ID.
 *
 * Call this once the session is resolved (e.g. in the AuthProvider
 * effect).  From this point on every new span will carry `user.id`.
 *
 * @param userId - Opaque user UUID (no PII).
 */
export function setUserId(userId: string): void {
  currentUserId = userId;
}

/**
 * Clear the user ID (call on sign-out).
 *
 * Spans created after this call will no longer have `user.id`.
 */
export function clearUserId(): void {
  currentUserId = undefined;
}

// ---------------------------------------------------------------------------
// SpanProcessor implementation
// ---------------------------------------------------------------------------

/**
 * A lightweight SpanProcessor that injects `user.id` and
 * `client.session.id` into every span at creation time.  It delegates
 * no export logic — pair it with a separate `BatchSpanProcessor` for
 * actual export.
 */
export class UserContextSpanProcessor implements SpanProcessor {
  /**
   * Called when a span is started.  Stamps the client session ID
   * unconditionally and the user ID when available.
   */
  onStart(span: Span, _parentContext: Context): void {
    span.setAttribute("client.session.id", clientSessionId());

    if (currentUserId) {
      span.setAttribute("user.id", currentUserId);
    }
  }

  /* The remaining lifecycle methods are no-ops — this processor only
     enriches spans, it never exports them. */

  onEnd(_span: ReadableSpan): void {
    // no-op
  }

  async shutdown(): Promise<void> {
    // no-op
  }

  async forceFlush(): Promise<void> {
    // no-op
  }
}
